import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, Lock, Mail, Phone, Briefcase, Trash2, Database, AlertTriangle, Save, MessageSquare } from 'lucide-react';
import BackupRestoreSection from '../../components/BackupRestoreSection';
import ContactMessages from './ContactMessages';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Input from '../../components/Input';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../services/authService';
import { useToast } from '../../contexts/ToastContext';
import Cookies from 'js-cookie';
import { API_BASE_URL } from '../../config/api';

const AdminProfile = () => {
  const { user, updateUser } = useAuth();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'profile');

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['profile', 'password', 'backups', 'danger', 'messages'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const selectTab = (tab) => {
    setActiveTab(tab);
    if (tab === 'profile') {
      setSearchParams({});
    } else {
      setSearchParams({ tab });
    }
  };
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    poste: user?.poste || '',
    phone: user?.phone || '',
  });

  // Load full user data including poste
  useEffect(() => {
    const loadUserData = async () => {
      try {
        // Get full user data from clients endpoint (admin is also a user)
        const token = Cookies.get('petrofeu_token');
        if (token && user?.id) {
          const response = await fetch(`${API_BASE_URL}/api/clients/${user.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.data) {
              setProfileData({
                name: data.data.name || user?.name || '',
                email: data.data.email || user?.email || '',
                poste: data.data.poste || '',
                phone: data.data.phone || '',
              });
            }
          }
        }
      } catch (error) {
        console.error('Failed to load user data:', error);
      }
    };
    loadUserData();
  }, [user]);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showPasswords, setShowPasswords] = useState({
    old: false,
    new: false,
    confirm: false
  });

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const token = Cookies.get('petrofeu_token');
      await authService.updateProfile(profileData, token);
      updateUser({ ...user, ...profileData });
      setMessage({ type: 'success', text: 'Profil mis à jour avec succès' });
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Erreur lors de la mise à jour' });
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return;
    }

    try {
      setSaving(true);
      const token = Cookies.get('petrofeu_token');
      await authService.updatePassword(
        user.id,
        passwordData.oldPassword,
        passwordData.newPassword,
        token
      );
      setMessage({ type: 'success', text: 'Mot de passe mis à jour avec succès' });
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      const errorMsg = error.message === 'Invalid password' 
        ? 'Mot de passe actuel incorrect' 
        : error.message || 'Erreur lors de la mise à jour du mot de passe';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAllData = async () => {
    try {
      setDeleting(true);
      const token = Cookies.get('petrofeu_token');
      const API_URL = API_BASE_URL;
      
      // Call backend API to delete all data
      const response = await fetch(`${API_URL}/api/admin/reset-data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        toast.success('Toutes les données ont été supprimées avec succès!');
        setMessage({ type: 'success', text: 'Base de données réinitialisée! Rechargez la page pour voir les changements.' });
        setShowDeleteConfirm(false);
        // Reload page after 2 seconds
        setTimeout(() => window.location.reload(), 2000);
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Failed to delete data:', error);
      toast.error('Erreur lors de la suppression des données');
      setMessage({ type: 'error', text: error.message || 'Erreur lors de la suppression' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Mon Profil</h1>
        <p className="text-gray-600 mt-1">Gérez vos informations personnelles</p>
      </div>

      {/* Tabs */}
      <Card>
        <div className="flex flex-wrap gap-1 sm:gap-0 sm:space-x-4 border-b border-gray-200">
          <button
            onClick={() => selectTab('profile')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'profile'
                ? 'text-primary-600 border-primary-600'
                : 'text-gray-600 border-transparent hover:text-gray-900'
            }`}
          >
            Informations personnelles
          </button>
          <button
            onClick={() => selectTab('password')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'password'
                ? 'text-primary-600 border-primary-600'
                : 'text-gray-600 border-transparent hover:text-gray-900'
            }`}
          >
            Mot de passe
          </button>
          <button
            onClick={() => selectTab('backups')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeTab === 'backups'
                ? 'text-primary-600 border-primary-600'
                : 'text-gray-600 border-transparent hover:text-gray-900'
            }`}
          >
            <Save className="w-4 h-4" />
            Sauvegardes
          </button>
          <button
            onClick={() => selectTab('danger')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 ${
              activeTab === 'danger'
                ? 'text-red-600 border-red-600'
                : 'text-gray-600 border-transparent hover:text-gray-900'
            }`}
          >
            Zone Dangereuse
          </button>
          <button
            onClick={() => selectTab('messages')}
            className={`px-4 py-2 font-medium transition-colors border-b-2 flex items-center gap-2 ${
              activeTab === 'messages'
                ? 'text-primary-600 border-primary-600'
                : 'text-gray-600 border-transparent hover:text-gray-900'
            }`}
          >
            <MessageSquare className="w-4 h-4" />
            Messages
          </button>
        </div>
      </Card>

      {/* Message */}
      {message.text && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-4 rounded-lg ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-red-50 border border-red-200 text-red-700'
          }`}
        >
          {message.text}
        </motion.div>
      )}

      {/* Profile Tab */}
      {activeTab === 'profile' && (
        <Card>
          <form onSubmit={handleProfileSubmit} className="space-y-6">
            <div className="flex items-center space-x-6">
              <div className="w-24 h-24 rounded-full bg-gradient-primary flex items-center justify-center text-white text-3xl font-bold">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{user?.name}</h3>
                <p className="text-gray-600">Administrateur</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Nom complet"
                name="name"
                value={profileData.name}
                onChange={handleProfileChange}
                icon={User}
                required
              />
              <Input
                label="Email"
                type="email"
                name="email"
                value={profileData.email}
                onChange={handleProfileChange}
                icon={Mail}
                required
              />
              <Input
                label="Poste"
                name="poste"
                value={profileData.poste}
                onChange={handleProfileChange}
                icon={Briefcase}
                placeholder="Ex: Responsable technique, Directeur..."
              />
              <Input
                label="Téléphone"
                name="phone"
                value={profileData.phone}
                onChange={handleProfileChange}
                icon={Phone}
              />
            </div>

            <div className="flex justify-end">
              <Button type="submit" variant="primary" loading={saving}>
                Enregistrer les modifications
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Password Tab */}
      {activeTab === 'password' && (
        <Card>
          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <Input
              label="Mot de passe actuel"
              type={showPasswords.old ? 'text' : 'password'}
              name="oldPassword"
              value={passwordData.oldPassword}
              onChange={handlePasswordChange}
              icon={Lock}
              placeholder="Saisissez votre mot de passe actuel"
              required
            />
            <Input
              label="Nouveau mot de passe"
              type={showPasswords.new ? 'text' : 'password'}
              name="newPassword"
              value={passwordData.newPassword}
              onChange={handlePasswordChange}
              icon={Lock}
              placeholder="Nouveau mot de passe (min. 8 caractères)"
              required
            />
            <Input
              label="Confirmer le nouveau mot de passe"
              type={showPasswords.confirm ? 'text' : 'password'}
              name="confirmPassword"
              value={passwordData.confirmPassword}
              onChange={handlePasswordChange}
              icon={Lock}
              placeholder="Confirmez le nouveau mot de passe"
              required
            />

            <div className="flex justify-end">
              <Button type="submit" variant="primary" loading={saving}>
                Changer le mot de passe
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Backups Tab */}
      {activeTab === 'backups' && (
        <BackupRestoreSection />
      )}

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <ContactMessages embedded />
      )}

      {/* Danger Zone Tab */}
      {activeTab === 'danger' && (
        <Card>
          <div className="space-y-6">
            {/* Warning Header */}
            <div className="flex items-start space-x-4 p-4 bg-red-50 border-2 border-red-200 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-lg font-semibold text-red-900 mb-1">Zone Dangereuse</h3>
                <p className="text-sm text-red-700">
                  Les actions dans cette section sont <strong>irréversibles</strong>. 
                  Procédez avec précaution.
                </p>
              </div>
            </div>

            {/* Delete All Data Section */}
            <div className="border-2 border-red-300 rounded-lg p-6 bg-white hover:shadow-lg transition-shadow">
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-red-100 rounded-lg">
                  <Database className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Réinitialiser la base de données
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Supprime <strong>TOUTES les données</strong> de l'application :
                  </p>
                  <ul className="text-sm text-gray-600 space-y-1 mb-4 ml-4">
                    <li>• Tous les clients</li>
                    <li>• Tous les équipements</li>
                    <li>• Tous les sites</li>
                    <li>• Toutes les maintenances</li>
                    <li>• Toutes les notifications</li>
                    <li>• Tous les rapports</li>
                    <li>• Tous les plans</li>
                    <li>• Toutes les soumissions de contact</li>
                  </ul>
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                    <p className="text-xs text-yellow-800 flex items-center">
                      <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                      <strong>Note:</strong> Seul le compte administrateur sera conservé.
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    icon={Trash2}
                    onClick={() => setShowDeleteConfirm(true)}
                    className="bg-red-600 hover:bg-red-700"
                  >
                    Supprimer toutes les données
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteAllData}
        title="⚠️ Supprimer toutes les données ?"
        message="Cette action supprimera DÉFINITIVEMENT tous les clients, équipements, sites, maintenances, rapports et plans. Seul le compte administrateur sera conservé. Cette action est IRRÉVERSIBLE. Êtes-vous absolument sûr ?"
        confirmText={deleting ? "Suppression..." : "Oui, tout supprimer"}
        cancelText="Annuler"
        variant="danger"
      />
    </div>
  );
};

export default AdminProfile;

